import KeyLine from 'components/shared/KeyLine';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { CalloutColumns, Margins } from 'types/constants';
import { cn, getCustomBackgroundColor, getSectionProps } from 'utils/styles';
import CalloutGridCard from './CalloutGridCard';
import CalloutGridCardIcon from './CalloutGridCardIcon';
import CalloutGridCardImage from './CalloutGridCardImage';
import CalloutGridCardContent from './CalloutGridCardContent';
import CalloutGridCardKeyline from './CalloutGridCardKeyline';
import { getGridClasses, getFlexWrapperClasses, isSingleContentItem, shouldShowKeylineUnderHeadline } from './utils';
import { CalloutGridCardProps, CalloutGridProps, CalloutGridVariants } from './types';

interface CalloutGridComponentProps extends CalloutGridProps {
  variant: CalloutGridVariants;
}

interface CalloutGridCardItemProps extends Pick<
  CalloutGridProps,
  'copyJustification' | 'keyline' | 'columns' | 'variant'
> {
  item: CalloutGridCardProps;
  index: number;
}

const getCalloutGridMargins = (margins: Margins) => {
  const marginStyles: { [key in Margins]: string } = {
    [Margins.NoTop]: cn('pt-0', 'pb-12 md:pb-15'),
    [Margins.NoBottom]: cn('pt-12 md:pt-20', 'pb-0'),
    [Margins.NoMargins]: cn('p-0', 'm-0'),
    [Margins.Centered]: cn('py-12 md:pt-20 md:pb-15'),
  };

  return marginStyles[margins];
};

// Sub-components
/**
 * Renders a keyline under the headline (mobile only)
 * Only renders if showKeyline is true
 */
const CalloutGridKeyline = ({ showKeyline }: { showKeyline: boolean }) => {
  if (!showKeyline) return null;

  return (
    <div className="block sm:hidden w-full">
      <KeyLine />
    </div>
  );
};

/**
 * Renders a single card item within the CalloutGrid
 * Handles the composition of keyline, icon/image, and content
 */
const CalloutGridCardItem = ({
  item,
  index,
  columns,
  keyline,
  copyJustification,
  variant,
}: CalloutGridCardItemProps) => {
  const isIconVariant = variant === CalloutGridVariants.Icon;
  const isImageVariant = variant === CalloutGridVariants.Image;
  const isLinkedCard = item.link != null;

  const singleContentItem = isSingleContentItem(item);
  const flexWrapperClasses = getFlexWrapperClasses(isIconVariant, copyJustification, singleContentItem);

  return (
    <CalloutGridCard
      key={index}
      card={item}
      index={index}
      columns={columns}
      keyline={keyline}
      copyJustification={copyJustification}
      variant={variant}>
      <CalloutGridCardKeyline keyline={keyline} variant={variant} />
      <div className={flexWrapperClasses}>
        {isIconVariant && (
          <CalloutGridCardIcon
            sys={item.sys}
            icon={item?.iconName}
            image={item?.image}
            copyJustification={copyJustification}
          />
        )}
        {isImageVariant && item.image && (
          <CalloutGridCardImage sys={item.sys} image={item.image} columns={columns} aspectRatio={item.aspectRatio} />
        )}
        <CalloutGridCardContent
          sys={item.sys}
          card={item}
          copyJustification={copyJustification}
          isLinkedCard={isLinkedCard}
        />
      </div>
    </CalloutGridCard>
  );
};

/**
 * CalloutGrid component - Renders a grid of callout cards with various layout options
 * Supports three variants: Basic, Icon, and Image
 * Handles responsive grid layouts, keylines, and content composition
 */
const CalloutGrid = ({
  sys,
  cards,
  sectionName,
  columns,
  color,
  keyline,
  margins,
  copyJustification,
  variant,
}: CalloutGridComponentProps) => {
  // Determine variant types for conditional rendering
  const isIconVariant = variant === CalloutGridVariants.Icon;
  const isBasicVariant = variant === CalloutGridVariants.Basic;

  // Validate columns value
  const validColumns = Object.values(CalloutColumns).includes(columns as CalloutColumns)
    ? (columns as CalloutColumns)
    : CalloutColumns.Two; // fallback to Two if invalid

  // Calculate display logic
  const showKeylineUnderHeadline = shouldShowKeylineUnderHeadline(keyline, isBasicVariant);
  const gridClasses = getGridClasses(validColumns, isIconVariant, isBasicVariant, keyline);
  const customMargins = getCalloutGridMargins(margins);

  return (
    <section {...getSectionProps(color, undefined, sectionName, customMargins)} style={getCustomBackgroundColor(color)}>
      <div className="content-wrapper">
        <div className="flex flex-col items-start w-full gap-8">
          <CalloutGridKeyline showKeyline={showKeylineUnderHeadline} />
          <div className={gridClasses} {...liveUpdateAttrs(sys?.id, 'cards')}>
            {cards.items.map((item, index) => (
              <CalloutGridCardItem
                key={index}
                item={item}
                index={index}
                columns={validColumns}
                keyline={keyline}
                copyJustification={copyJustification}
                variant={variant}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalloutGrid;
